import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../servizi/api.ts'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella, type TonoEtichetta } from '../../componenti/base.tsx'
import { Attesa, Avviso, Caselle, Vuoto } from '../../componenti/Pezzi.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import { IconaPiu } from '../../componenti/Icone.tsx'

/**
 * Linguetta «Orari e chiusure» della pagina della sede (556; docs/15-calendari-sedi.md, fase 1).
 *
 * L'orario settimanale (piu' fasce al giorno, «al pubblico» o «solo interno»), gli orari di un periodo
 * come quello estivo, le chiusure (mezze giornate, ponti, festa patronale ogni anno) con le aperture
 * straordinarie, e l'anteprima delle festivita' dell'anno. Lo leggono tutti; lo modifica chi ha
 * `sede.agenda` su questa sede. Con questi dati la programmazione di mediazioni e ricontatti avvisa
 * quando un impegno cade con la sede chiusa.
 */

type Fascia = {
  id: string; giorno_settimana: number; dalle: string; alle: string
  valido_dal: string | null; valido_al: string | null; pubblico: boolean; nota: string | null
}
type TipoChiusura = 'festa_patronale' | 'chiusura' | 'ponte' | 'apertura_straordinaria'
type Chiusura = {
  id: string; sede_id: string | null; tutte_le_sedi: boolean; tipo: TipoChiusura; dal: string; al: string
  dalle: string | null; alle: string | null; ogni_anno: boolean; motivo: string; passata: boolean
}
export type OrariSede = {
  sede: { id: string; codice: string; nome: string; tipo: string; attiva: boolean
    sede_padre_id: string | null; sede_padre_nome: string | null }
  orari: Fascia[]
  chiusure: Chiusura[]
  puo_scrivere: boolean
  puo_scrivere_tutte: boolean
}
type Festivita = {
  anno: number
  nazionali: { giorno: string; codice: string; nome: string; giorno_settimana: number }[]
  della_sede: { id: string; tutte_le_sedi: boolean; tipo: TipoChiusura; dal: string; al: string
    dalle: string | null; alle: string | null; ogni_anno: boolean; motivo: string }[]
}

const GIORNI = [
  { valore: 1, nome: 'Lunedì', corto: 'lun' }, { valore: 2, nome: 'Martedì', corto: 'mar' },
  { valore: 3, nome: 'Mercoledì', corto: 'mer' }, { valore: 4, nome: 'Giovedì', corto: 'gio' },
  { valore: 5, nome: 'Venerdì', corto: 'ven' }, { valore: 6, nome: 'Sabato', corto: 'sab' },
  { valore: 7, nome: 'Domenica', corto: 'dom' },
]
const nomeGiorno = (n: number) => GIORNI.find((g) => g.valore === n)?.nome ?? String(n)

export const TIPI_CHIUSURA: { valore: TipoChiusura; etichetta: string; tono: TonoEtichetta }[] = [
  { valore: 'chiusura', etichetta: 'Chiusura', tono: 'attenzione' },
  { valore: 'ponte', etichetta: 'Ponte', tono: 'attenzione' },
  { valore: 'festa_patronale', etichetta: 'Festa patronale', tono: 'informativo' },
  { valore: 'apertura_straordinaria', etichetta: 'Apertura straordinaria', tono: 'positivo' },
]
const tipo = (t: TipoChiusura) => TIPI_CHIUSURA.find((x) => x.valore === t)!

/** 2027-07-01 → 01/07/2027; con `corto`, 01/07. */
const data = (g: string, corto = false) => {
  const [a, m, d] = g.slice(0, 10).split('-')
  return corto ? `${d}/${m}` : `${d}/${m}/${a}`
}
/** Giorno della settimana di un AAAA-MM-GG, senza passare dal fuso del browser. */
const giornoDi = (g: string) => {
  const [a, m, d] = g.split('-').map(Number)
  return GIORNI[(new Date(Date.UTC(a, m - 1, d)).getUTCDay() + 6) % 7].nome.toLowerCase()
}

/** «il 29/01, ogni anno», «dal 07/12/2027 al 08/12/2027, dalle 14:00 alle 18:00». */
function quandoChiusura(c: { dal: string; al: string; dalle: string | null; alle: string | null; ogni_anno: boolean }) {
  const giorni = c.dal === c.al
    ? `${giornoDi(c.dal)} ${data(c.dal, c.ogni_anno)}`
    : `dal ${data(c.dal, c.ogni_anno)} al ${data(c.al, c.ogni_anno)}`
  const ore = c.dalle && c.alle ? `, dalle ${c.dalle} alle ${c.alle}` : ''
  return `${giorni}${ore}${c.ogni_anno ? ', ogni anno' : ''}`
}

/** Lo stesso giorno l'anno dopo; il 29 febbraio diventa il 28, come nel database. */
const annoDopo = (g: string) => {
  const [a, m, d] = g.split('-')
  return `${Number(a) + 1}-${m}-${m === '02' && d === '29' ? '28' : d}`
}

const oggiARoma = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Rome' }).format(new Date())

export function OrariChiusure({ sedeId }: { sedeId: string }) {
  const [dati, setDati] = useState<OrariSede | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [fascia, setFascia] = useState<Fascia | 'nuova' | null>(null)
  const [chiusura, setChiusura] = useState<Chiusura | 'nuova' | null>(null)
  const [daAnnullare, setDaAnnullare] = useState<Chiusura | null>(null)
  const [daTogliere, setDaTogliere] = useState<Fascia | null>(null)
  const [daCopiare, setDaCopiare] = useState<{ dal: string; al: string } | null>(null)
  const [passate, setPassate] = useState(false)
  const [versione, setVersione] = useState(0)

  const carica = useCallback(() => {
    api.get<OrariSede>(`/sedi/${sedeId}/orari`)
      .then((d) => { setDati(d); setErrore(null) })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [sedeId])
  useEffect(carica, [carica])

  const fatto = (testo: string) => {
    setFascia(null); setChiusura(null); setDaAnnullare(null); setDaTogliere(null); setDaCopiare(null)
    setEsito(testo); setVersione((v) => v + 1); carica()
  }

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa />

  const scrive = dati.puo_scrivere
  const settimanali = dati.orari.filter((o) => !o.valido_dal)
  const periodi = [...new Map(dati.orari.filter((o) => o.valido_dal)
    .map((o) => [`${o.valido_dal}|${o.valido_al}`, { dal: o.valido_dal!, al: o.valido_al! }])).values()]
  const chiusure = dati.chiusure.filter((c) => passate || !c.passata)
  const quantePassate = dati.chiusure.filter((c) => c.passata).length

  return (
    <div className="sedi-orari">
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {!scrive && (
        <p className="tenue sedi-orari-nota">
          Orari e chiusure li modifica chi coordina la sede. Con questi dati il gestionale avvisa quando
          una mediazione o un ricontatto cade con la sede chiusa.
        </p>
      )}

      <Riquadro
        titolo="Orario settimanale"
        azioni={scrive && (
          <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} onClick={() => setFascia('nuova')}>
            Aggiungi una fascia
          </Bottone>
        )}
      >
        {settimanali.length
          ? <Settimana fasce={settimanali} scrive={scrive} modifica={setFascia} togli={setDaTogliere} />
          : <Vuoto testo="Orario non indicato. Finché manca, la programmazione avvisa solo per chiusure e festività." />}
      </Riquadro>

      {periodi.map((p) => (
        <Riquadro key={`${p.dal}|${p.al}`} titolo={`Orario dal ${data(p.dal)} al ${data(p.al)}`}
                  azioni={scrive && (
                    <Bottone variante="secondario" dimensione="piccolo" onClick={() => setDaCopiare(p)}>
                      Copia nell’anno dopo
                    </Bottone>
                  )}>
          <p className="tenue sedi-orari-nota">
            In questi giorni vale solo questo orario, al posto di quello settimanale.
          </p>
          <Settimana fasce={dati.orari.filter((o) => o.valido_dal === p.dal && o.valido_al === p.al)}
                     scrive={scrive} modifica={setFascia} togli={setDaTogliere} />
        </Riquadro>
      ))}

      <Riquadro
        titolo="Chiusure e aperture straordinarie"
        azioni={<>
          {quantePassate > 0 && (
            <Spunta etichetta={`Mostra le passate (${quantePassate})`} valore={passate} cambia={setPassate} />
          )}
          {scrive && (
            <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} onClick={() => setChiusura('nuova')}>
              Aggiungi
            </Bottone>
          )}
        </>}
        denso
      >
        <Tabella intestazioni={['Quando', 'Tipo', 'Motivo', 'Vale per', '']}
                 vuoto={<Vuoto testo="Nessuna chiusura in programma." />}>
          {chiusure.map((c) => {
            const t = tipo(c.tipo)
            const modificabile = c.tutte_le_sedi ? dati.puo_scrivere_tutte : scrive
            return (
              <tr key={c.id}>
                <td data-etichetta="Quando">
                  {quandoChiusura(c)}
                  {c.passata && <> <Etichetta>passata</Etichetta></>}
                </td>
                <td data-etichetta="Tipo"><Etichetta tono={t.tono}>{t.etichetta}</Etichetta></td>
                <td data-etichetta="Motivo">{c.motivo}</td>
                <td data-etichetta="Vale per">{c.tutte_le_sedi ? 'tutte le sedi' : 'questa sede'}</td>
                <td>
                  {modificabile && (
                    <span className="riga">
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setChiusura(c)}>Modifica</Bottone>
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setDaAnnullare(c)}>Annulla</Bottone>
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </Tabella>
      </Riquadro>

      <AnteprimaFestivita sedeId={sedeId} versione={versione} />

      {fascia && (
        <ModuloFascia sedeId={sedeId} fascia={fascia === 'nuova' ? null : fascia}
                      chiudi={() => setFascia(null)} fatto={fatto} />
      )}
      {chiusura && (
        <ModuloChiusura sedeId={sedeId} chiusura={chiusura === 'nuova' ? null : chiusura}
                        puoTutte={dati.puo_scrivere_tutte} chiudi={() => setChiusura(null)} fatto={fatto} />
      )}
      {daAnnullare && (
        <Conferma titolo="Annulla la chiusura" comando="Annulla la chiusura" chiudi={() => setDaAnnullare(null)}
                  esegui={() => api.post(`/sedi/${sedeId}/chiusure/${daAnnullare.id}/annulla`)}
                  fatto={() => fatto('Chiusura annullata.')}>
          <p>
            «{daAnnullare.motivo}», {quandoChiusura(daAnnullare)}: non conterà più. Resta traccia nel registro
            eventi.
          </p>
        </Conferma>
      )}
      {daCopiare && (
        <Conferma titolo="Copia l’orario nell’anno dopo" comando="Copia" tono="primario"
                  chiudi={() => setDaCopiare(null)}
                  esegui={() => api.post(`/sedi/${sedeId}/orari/copia-periodo`,
                    { valido_dal: daCopiare.dal, valido_al: daCopiare.al })}
                  fatto={() => fatto(`Orario copiato dal ${data(annoDopo(daCopiare.dal))} al ${data(annoDopo(daCopiare.al))}.`)}>
          <p>
            Le fasce dell’orario dal {data(daCopiare.dal)} al {data(daCopiare.al)} valgono anche dal{' '}
            {data(annoDopo(daCopiare.dal))} al {data(annoDopo(daCopiare.al))}. Dopo si modificano una per una.
          </p>
        </Conferma>
      )}
      {daTogliere && (
        <Conferma titolo="Togli la fascia" comando="Togli la fascia" chiudi={() => setDaTogliere(null)}
                  esegui={() => api.elimina(`/sedi/${sedeId}/orari/${daTogliere.id}`)}
                  fatto={() => fatto('Fascia tolta.')}>
          <p>{nomeGiorno(daTogliere.giorno_settimana)} {daTogliere.dalle}–{daTogliere.alle}: la tolgo dall’orario?</p>
        </Conferma>
      )}
    </div>
  )
}

/** I giorni della settimana, ciascuno con le sue fasce; i giorni senza fasce sono chiusi. */
function Settimana({ fasce, scrive, modifica, togli }: {
  fasce: Fascia[]; scrive: boolean; modifica: (f: Fascia) => void; togli: (f: Fascia) => void
}) {
  return (
    <dl className="sedi-settimana">
      {GIORNI.map((g) => {
        const delGiorno = fasce.filter((f) => f.giorno_settimana === g.valore)
        return (
          <div key={g.valore} className="sedi-giorno">
            <dt>{g.nome}</dt>
            <dd>
              {delGiorno.length === 0 && <span className="tenue">chiusa</span>}
              {delGiorno.map((f) => (
                <span key={f.id} className="sedi-fascia">
                  <span className="mono">{f.dalle}–{f.alle}</span>
                  {!f.pubblico && <Etichetta tono="informativo">solo interno</Etichetta>}
                  {f.nota && <span className="piccolo tenue">{f.nota}</span>}
                  {scrive && (
                    <span className="riga">
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => modifica(f)}
                               aria-label={`Modifica la fascia di ${g.nome.toLowerCase()} ${f.dalle}–${f.alle}`}>
                        Modifica
                      </Bottone>
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => togli(f)}
                               aria-label={`Togli la fascia di ${g.nome.toLowerCase()} ${f.dalle}–${f.alle}`}>
                        Togli
                      </Bottone>
                    </span>
                  )}
                </span>
              ))}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/** Le festivita' dell'anno: nazionali e della sede (patrono, chiusure, ponti, aperture). */
function AnteprimaFestivita({ sedeId, versione }: { sedeId: string; versione: number }) {
  const [anno, setAnno] = useState(() => Number(oggiARoma().slice(0, 4)))
  const [dati, setDati] = useState<Festivita | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    let vivo = true
    api.get<Festivita>(`/festivita?anno=${anno}&sede_id=${sedeId}`)
      .then((d) => { if (vivo) { setDati(d); setErrore(null) } })
      .catch((e) => { if (vivo) setErrore(messaggioErrore(e)) })
    return () => { vivo = false }
  }, [anno, sedeId, versione])

  // Un solo elenco in ordine di giorno: le nazionali e quelle della sede.
  const voci = useMemo(() => (dati ? [
    ...dati.nazionali.map((f) => ({ chiave: `n-${f.codice}`, giorno: f.giorno, quando: `${giornoDi(f.giorno)} ${data(f.giorno)}`,
      nome: f.nome, etichetta: <Etichetta>nazionale</Etichetta> })),
    ...dati.della_sede.map((c) => {
      const t = tipo(c.tipo)
      return { chiave: `s-${c.id}`, giorno: c.dal, quando: quandoChiusura({ ...c, ogni_anno: false }), nome: c.motivo,
        etichetta: <Etichetta tono={t.tono}>{c.tutte_le_sedi ? `${t.etichetta} (tutte le sedi)` : t.etichetta}</Etichetta> }
    }),
  ].sort((a, b) => a.giorno.localeCompare(b.giorno) || a.chiave.localeCompare(b.chiave)) : []), [dati])

  return (
    <Riquadro
      titolo={`Festività del ${anno}`}
      azioni={(
        <span className="riga">
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAnno((a) => a - 1)}
                   aria-label="Anno precedente">‹ {anno - 1}</Bottone>
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAnno((a) => a + 1)}
                   aria-label="Anno successivo">{anno + 1} ›</Bottone>
        </span>
      )}
      denso
    >
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <ul className="sedi-festivita">
          {voci.map((v) => (
            <li key={v.chiave}>
              <span className="sedi-festivita-giorno">{v.quando}</span>
              <span>{v.nome}</span>
              {v.etichetta}
            </li>
          ))}
        </ul>
      )}
    </Riquadro>
  )
}

/** Fascia nuova (anche su piu' giorni) o modifica di una fascia. */
function ModuloFascia({ sedeId, fascia, chiudi, fatto }: {
  sedeId: string; fascia: Fascia | null; chiudi: () => void; fatto: (t: string) => void
}) {
  const [giorni, setGiorni] = useState<string[]>(fascia ? [String(fascia.giorno_settimana)] : ['1', '2', '3', '4', '5'])
  const [giorno, setGiorno] = useState(String(fascia?.giorno_settimana ?? 1))
  const [dalle, setDalle] = useState(fascia?.dalle ?? '09:00')
  const [alle, setAlle] = useState(fascia?.alle ?? '13:00')
  const [pubblico, setPubblico] = useState(fascia ? (fascia.pubblico ? 'si' : 'no') : 'si')
  const [periodo, setPeriodo] = useState(!!fascia?.valido_dal)
  const [dal, setDal] = useState(fascia?.valido_dal ?? '')
  const [al, setAl] = useState(fascia?.valido_al ?? '')
  const [nota, setNota] = useState(fascia?.nota ?? '')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    setErrore(null)
    if (!fascia && !giorni.length) { setErrore('Scegli almeno un giorno.'); return }
    if (periodo && (!dal || !al)) { setErrore('Indica il primo e l’ultimo giorno del periodo.'); return }
    const comuni = {
      dalle, alle, pubblico: pubblico === 'si', nota: nota.trim() || null,
      valido_dal: periodo ? dal : null, valido_al: periodo ? al : null,
    }
    setInCorso(true)
    try {
      if (fascia) {
        await api.patch(`/sedi/${sedeId}/orari/${fascia.id}`, { ...comuni, giorno_settimana: Number(giorno) })
        fatto('Fascia aggiornata.')
      } else {
        await api.post(`/sedi/${sedeId}/orari`, { ...comuni, giorni: giorni.map(Number) })
        fatto(giorni.length === 1 ? 'Fascia aggiunta.' : `Fascia aggiunta a ${giorni.length} giorni.`)
      }
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={fascia ? 'Modifica la fascia' : 'Aggiungi una fascia all’orario'} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>
                {fascia ? 'Salva' : 'Aggiungi'}
              </Bottone>
            </>}>
      {fascia
        ? <Scelta etichetta="Giorno" valore={giorno} cambia={setGiorno}
                  opzioni={GIORNI.map((g) => ({ valore: String(g.valore), etichetta: g.nome }))} />
        : (
          <fieldset className="gruppo-caselle">
            <legend>Giorni</legend>
            <Caselle voci={GIORNI.map((g) => ({ codice: String(g.valore), nome: g.nome }))}
                     scelte={giorni} cambia={setGiorni} />
          </fieldset>
        )}
      <div className="griglia-modulo">
        <Campo etichetta="Dalle" tipo="time" obbligatorio valore={dalle} cambia={setDalle} passi={300} />
        <Campo etichetta="Alle" tipo="time" obbligatorio valore={alle} cambia={setAlle} passi={300} />
        <Scelta etichetta="Aperta" valore={pubblico} cambia={setPubblico}
                opzioni={[{ valore: 'si', etichetta: 'Al pubblico' }, { valore: 'no', etichetta: 'Solo interno' }]}
                aiuto="Solo interno: c’è il personale, ma non si ricevono persone." />
      </div>
      <Spunta etichetta="Vale solo in un periodo (per esempio l’orario estivo)" valore={periodo} cambia={setPeriodo}
              aiuto="Nei giorni del periodo valgono solo le fasce del periodo, al posto dell’orario settimanale." />
      {periodo && (
        <div className="griglia-modulo">
          <Campo etichetta="Dal" tipo="date" obbligatorio valore={dal} cambia={setDal} />
          <Campo etichetta="Al" tipo="date" obbligatorio valore={al} min={dal || undefined} cambia={setAl} />
        </div>
      )}
      <Campo etichetta="Nota" valore={nota} cambia={setNota} maxLength={200}
             aiuto="Facoltativa, per esempio «solo su appuntamento». La leggono tutti." />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/** Chiusura, ponte, festa patronale o apertura straordinaria: nuova o da modificare. */
function ModuloChiusura({ sedeId, chiusura, puoTutte, chiudi, fatto }: {
  sedeId: string; chiusura: Chiusura | null; puoTutte: boolean; chiudi: () => void; fatto: (t: string) => void
}) {
  const [tipoScelto, setTipo] = useState<TipoChiusura>(chiusura?.tipo ?? 'chiusura')
  const [dal, setDal] = useState(chiusura?.dal ?? '')
  const [al, setAl] = useState(chiusura?.al ?? '')
  const [parte, setParte] = useState(!!chiusura?.dalle)
  const [dalle, setDalle] = useState(chiusura?.dalle ?? '14:00')
  const [alle, setAlle] = useState(chiusura?.alle ?? '18:00')
  const [ogniAnno, setOgniAnno] = useState(chiusura?.ogni_anno ?? false)
  const [motivo, setMotivo] = useState(chiusura?.motivo ?? '')
  const [tutte, setTutte] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  // La festa patronale torna ogni anno: la spunta parte accesa.
  const cambiaTipo = (t: string) => {
    setTipo(t as TipoChiusura)
    if (!chiusura && t === 'festa_patronale') setOgniAnno(true)
  }

  async function salva() {
    setErrore(null)
    if (!dal) { setErrore('Indica il giorno.'); return }
    if (motivo.trim().length < 3) { setErrore('Scrivi il motivo, per esempio «disinfestazione».'); return }
    const corpo = {
      tipo: tipoScelto, dal, al: al || dal, dalle: parte ? dalle : null, alle: parte ? alle : null,
      ogni_anno: ogniAnno, motivo: motivo.trim(),
    }
    setInCorso(true)
    try {
      const r = chiusura
        ? await api.patch<{ impegni?: number }>(`/sedi/${sedeId}/chiusure/${chiusura.id}`, corpo)
        : await api.post<{ impegni: number }>(`/sedi/${sedeId}/chiusure`, { ...corpo, tutte_le_sedi: tutte })
      const impegni = r.impegni ?? 0
      const base = chiusura ? 'Modifica salvata.' : `${tipo(tipoScelto).etichetta} aggiunta.`
      fatto(impegni
        ? `${base} In quei giorni ${impegni === 1 ? 'c’è un impegno già fissato' : `ci sono ${impegni} impegni già fissati`}: `
          + 'chi li ha programmati e chi segue i ricontatti ricevono un avviso. Non è stato annullato niente.'
        : base)
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale larga titolo={chiusura ? 'Modifica la chiusura' : 'Aggiungi una chiusura o un’apertura straordinaria'}
            chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>
                {chiusura ? 'Salva' : 'Aggiungi'}
              </Bottone>
            </>}>
      <div className="griglia-modulo">
        <Scelta etichetta="Tipo" valore={tipoScelto} cambia={cambiaTipo}
                opzioni={TIPI_CHIUSURA.map((t) => ({ valore: t.valore, etichetta: t.etichetta }))}
                aiuto={tipoScelto === 'apertura_straordinaria'
                  ? 'La sede è aperta anche se l’orario, una chiusura o una festività direbbero il contrario.'
                  : undefined} />
        <Campo etichetta="Dal" tipo="date" obbligatorio valore={dal} cambia={setDal} />
        <Campo etichetta="Al" tipo="date" valore={al} min={dal || undefined} cambia={setAl}
               aiuto="Vuoto: un giorno solo." />
      </div>
      <Spunta etichetta="Solo una parte del giorno" valore={parte} cambia={setParte} />
      {parte && (
        <div className="griglia-modulo">
          <Campo etichetta="Dalle" tipo="time" obbligatorio valore={dalle} cambia={setDalle} passi={300} />
          <Campo etichetta="Alle" tipo="time" obbligatorio valore={alle} cambia={setAlle} passi={300} />
        </div>
      )}
      <Spunta etichetta="Ogni anno, negli stessi giorni" valore={ogniAnno} cambia={setOgniAnno}
              aiuto="Per il santo patrono. Il periodo comincia e finisce nello stesso anno." />
      <Campo etichetta="Motivo" obbligatorio valore={motivo} cambia={setMotivo} maxLength={200}
             segnaposto="Per esempio: disinfestazione, San Costanzo, assemblea del personale"
             aiuto="Lo leggono tutti quelli che usano il gestionale: non scrivere nomi di persone." />
      {!chiusura && puoTutte && (
        <Spunta etichetta="Vale per tutte le sedi" valore={tutte} cambia={setTutte}
                aiuto="Per esempio un ponte deciso dalla Direzione per tutta CIDIS." />
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/** Conferma di un'azione con una frase che dice che cosa succede. */
function Conferma({ titolo, comando, tono = 'critico', chiudi, esegui, fatto, children }: {
  titolo: string; comando: string; tono?: 'critico' | 'primario'; chiudi: () => void
  esegui: () => Promise<unknown>; fatto: () => void; children: React.ReactNode
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  async function vai() {
    setErrore(null); setInCorso(true)
    try {
      await esegui()
      fatto()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }
  return (
    <Modale titolo={titolo} chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante={tono} inCorso={inCorso} onClick={() => void vai()}>{comando}</Bottone>
            </>}>
      {children}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
